import { useEffect, useMemo, useState } from 'react';
import { Modale } from '../ui/Modale';
import { PaveSignature } from '../ui/PaveSignature';
import { Chargement, MessageErreur } from '../ui/Primitives';
import { Icone } from '../Icone';
import { ErreurApi } from '../../lib/api';
import {
  LIBELLES_SITUATION,
  useCompteRenduImmersion,
  useEvaluerImmersion,
  type LigneImmersion,
  type SituationEvaluation,
} from '../../lib/useMatching';
import { aujourdhui } from '../../lib/dates-civiles';

/** Instant ISO ramené au jour civil réunionnais attendu par `<input type="date">`. */
function jourSaisie(valeur: string | null | undefined): string {
  if (!valeur) return '';
  const instant = new Date(valeur);
  if (Number.isNaN(instant.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Indian/Reunion' }).format(instant);
}

function jourLisible(valeur: string | null | undefined): string {
  if (!valeur) return '—';
  const instant = new Date(valeur);
  if (Number.isNaN(instant.getTime())) return '—';
  return instant.toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

interface Saisie {
  dateVisite: string;
  compteRendu: string;
  problematiques: string;
  solutions: string;
  situation: SituationEvaluation;
  dateRupture: string;
  motifRupture: string;
  observations: string;
  faitA: string;
  faitLe: string;
  nomEntreprise: string;
  fonctionEntreprise: string;
  nomApprenant: string;
  nomOrganisme: string;
}

const SAISIE_VIDE: Saisie = {
  dateVisite: '',
  compteRendu: '',
  problematiques: '',
  solutions: '',
  situation: 'NORMAL',
  dateRupture: '',
  motifRupture: '',
  observations: '',
  faitA: '',
  faitLe: '',
  nomEntreprise: '',
  fonctionEntreprise: '',
  nomApprenant: '',
  nomOrganisme: '',
};

const SITUATIONS: SituationEvaluation[] = ['NORMAL', 'RUPTURE_ENVISAGEE', 'RUPTURE'];

const CLASSE_CHAMP =
  'w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-codialis-navy ' +
  'focus:border-codialis-sky focus:bg-white focus:outline-none focus:ring-1 focus:ring-codialis-sky';

/**
 * Compte rendu d'entretien en entreprise (CDC §5.1).
 *
 * Un seul compte rendu par immersion : la fenêtre sert aussi bien à l'établir
 * qu'à le corriger, et la correction régénère le même document. Rien de ce que
 * la plateforme sait déjà n'est redemandé — identité, formation, entreprise et
 * tuteur arrivent préremplis de l'API, et restent modifiables là où la personne
 * rencontrée sur place peut différer de la fiche.
 */
export function ModaleCompteRendu({
  immersion,
  onFermer,
  onVoirCompteRendu,
}: {
  immersion: LigneImmersion;
  onFermer: () => void;
  onVoirCompteRendu: () => void;
}): JSX.Element {
  const { data, isLoading, error } = useCompteRenduImmersion(immersion.id);
  const enregistrer = useEvaluerImmersion();

  const [saisie, setSaisie] = useState<Saisie>(SAISIE_VIDE);
  // `undefined` = tracé inchangé, `null` = signature effacée, chaîne = nouveau
  // tracé. La distinction compte : corriger une phrase ne doit pas effacer
  // trois signatures déjà recueillies.
  const [traces, setTraces] = useState<{
    entreprise?: string | null;
    apprenant?: string | null;
    organisme?: string | null;
  }>({});
  const [confirmation, setConfirmation] = useState(false);

  const existant = data?.evaluation ?? null;

  // Le formulaire se remplit une fois les données connues, jamais à chaque
  // rendu : la frappe de la personne ne doit pas être écrasée par le
  // préremplissage.
  useEffect(() => {
    if (!data) return;
    const prerempli = data.prerempli;
    const evaluation = data.evaluation;
    setSaisie({
      dateVisite: jourSaisie(evaluation?.dateVisite) || aujourdhui(),
      compteRendu: evaluation?.compteRendu ?? '',
      problematiques: evaluation?.problematiques ?? '',
      solutions: evaluation?.solutions ?? '',
      situation: evaluation?.situation ?? 'NORMAL',
      dateRupture: jourSaisie(evaluation?.dateRupture),
      motifRupture: evaluation?.motifRupture ?? '',
      observations: evaluation?.observations ?? '',
      faitA: evaluation?.faitA ?? prerempli.organisme.lieu,
      faitLe: jourSaisie(evaluation?.faitLe) || aujourdhui(),
      // Nom et fonction du tuteur repris de la fiche entreprise : la personne
      // qui signe n'a pas à les ressaisir. Modifiables, parce que le tuteur de
      // la fiche n'est pas toujours celui qu'on rencontre sur place.
      nomEntreprise: evaluation?.nomEntreprise ?? prerempli.entreprise.tuteur,
      fonctionEntreprise: evaluation?.fonctionEntreprise ?? prerempli.entreprise.fonctionTuteur,
      nomApprenant:
        evaluation?.nomApprenant ?? `${prerempli.apprenant.prenom} ${prerempli.apprenant.nom}`.trim(),
      nomOrganisme: evaluation?.nomOrganisme ?? prerempli.organisme.representant,
    });
    setTraces({});
  }, [data]);

  const rupture = saisie.situation !== 'NORMAL';

  const messageErreur = useMemo(() => {
    if (enregistrer.error instanceof ErreurApi) return enregistrer.error.message;
    if (enregistrer.error) return 'Le compte rendu n’a pas pu être enregistré.';
    return null;
  }, [enregistrer.error]);

  function valider(): void {
    enregistrer.mutate(
      {
        id: immersion.id,
        dateVisite: saisie.dateVisite,
        compteRendu: saisie.compteRendu,
        problematiques: saisie.problematiques,
        solutions: saisie.solutions,
        situation: saisie.situation,
        dateRupture: rupture && saisie.dateRupture ? saisie.dateRupture : null,
        motifRupture: rupture ? saisie.motifRupture : null,
        observations: saisie.observations,
        faitA: saisie.faitA,
        faitLe: saisie.faitLe || null,
        nomEntreprise: saisie.nomEntreprise,
        fonctionEntreprise: saisie.fonctionEntreprise,
        nomApprenant: saisie.nomApprenant,
        nomOrganisme: saisie.nomOrganisme,
        ...(traces.entreprise === undefined ? {} : { signatureEntreprise: traces.entreprise }),
        ...(traces.apprenant === undefined ? {} : { signatureApprenant: traces.apprenant }),
        ...(traces.organisme === undefined ? {} : { signatureOrganisme: traces.organisme }),
      },
      { onSuccess: () => setConfirmation(true) },
    );
  }

  return (
    <Modale
      ouverte
      onFermer={onFermer}
      titre="Compte rendu d’entretien en entreprise"
      sousTitre={`${immersion.candidat} · ${immersion.entreprise}`}
      largeur="max-w-4xl"
    >
      {confirmation ? (
        <div className="space-y-4 p-4 sm:p-6">
          <div className="flex items-start gap-3 rounded-xl bg-codialis-sand p-4">
            <Icone nom="document" className="mt-0.5 h-5 w-5 shrink-0 text-codialis-sky" />
            <div>
              <p className="text-sm font-semibold text-codialis-navy">
                Compte rendu généré avec succès
              </p>
              <p className="mt-1 text-xs text-codialis-gray">
                Le document est classé au dossier de {immersion.candidat} et consultable depuis le
                dossier de {immersion.entreprise}.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={onFermer}
              className="min-h-[44px] rounded-xl px-4 py-2.5 text-sm font-semibold text-codialis-navy hover:bg-gray-50"
            >
              Fermer
            </button>
            <button
              type="button"
              onClick={onVoirCompteRendu}
              className="min-h-[44px] rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white hover:bg-codialis-deep"
            >
              Voir le compte rendu
            </button>
          </div>
        </div>
      ) : isLoading ? (
        <Chargement libelle="Chargement du compte rendu…" />
      ) : error ? (
        <div className="p-4 sm:p-6">
          <MessageErreur
            message={
              error instanceof ErreurApi
                ? error.message
                : 'Le compte rendu n’a pas pu être chargé.'
            }
          />
        </div>
      ) : (
        <div className="max-h-[75vh] space-y-6 overflow-y-auto p-4 sm:p-6">
          {/* Ce que la plateforme sait déjà : rappelé, pas redemandé. */}
          <section>
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
              Informations du stagiaire / apprenant
            </h3>
            <dl className="mt-2 grid grid-cols-1 gap-3 rounded-xl bg-gray-50 p-4 text-sm sm:grid-cols-2">
              <Donnee libelle="Nom" valeur={data?.prerempli.apprenant.nom} />
              <Donnee libelle="Prénom" valeur={data?.prerempli.apprenant.prenom} />
              <Donnee
                libelle="Date d’entrée en entreprise"
                valeur={jourLisible(data?.prerempli.apprenant.dateEntree)}
              />
              <Donnee libelle="Formation en cours" valeur={data?.prerempli.apprenant.formation} />
            </dl>
          </section>

          <section>
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
              Informations sur l’entreprise
            </h3>
            <dl className="mt-2 grid grid-cols-1 gap-3 rounded-xl bg-gray-50 p-4 text-sm sm:grid-cols-2">
              <Donnee
                libelle="Nom de l’entreprise"
                valeur={data?.prerempli.entreprise.denomination}
              />
              <Donnee libelle="Nom et prénom du tuteur" valeur={data?.prerempli.entreprise.tuteur} />
              <Donnee
                libelle="Fonction du tuteur"
                valeur={data?.prerempli.entreprise.fonctionTuteur}
              />
            </dl>
          </section>

          <section className="space-y-4">
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
              Objet de la visite
            </h3>
            <Champ libelle="Date de la visite">
              <input
                type="date"
                value={saisie.dateVisite}
                onChange={(evenement) =>
                  setSaisie((etat) => ({ ...etat, dateVisite: evenement.target.value }))
                }
                className={`${CLASSE_CHAMP} sm:max-w-xs`}
              />
            </Champ>
            <Champ libelle="Compte rendu de l’entretien">
              <textarea
                rows={6}
                value={saisie.compteRendu}
                onChange={(evenement) =>
                  setSaisie((etat) => ({ ...etat, compteRendu: evenement.target.value }))
                }
                className={CLASSE_CHAMP}
              />
            </Champ>
          </section>

          <section className="space-y-4">
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
              Suivi
            </h3>
            <Champ
              libelle="Problématiques rencontrées"
              aide="Difficultés rencontrées pendant la période en entreprise."
            >
              <textarea
                rows={4}
                value={saisie.problematiques}
                onChange={(evenement) =>
                  setSaisie((etat) => ({ ...etat, problematiques: evenement.target.value }))
                }
                className={CLASSE_CHAMP}
              />
            </Champ>
            <Champ
              libelle="Solutions proposées"
              aide="Actions ou solutions envisagées pour résoudre les difficultés."
            >
              <textarea
                rows={4}
                value={saisie.solutions}
                onChange={(evenement) =>
                  setSaisie((etat) => ({ ...etat, solutions: evenement.target.value }))
                }
                className={CLASSE_CHAMP}
              />
            </Champ>
          </section>

          <section className="space-y-3">
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
              Situation de la formation
            </h3>
            <div className="space-y-2">
              {SITUATIONS.map((situation) => (
                <label
                  key={situation}
                  className="flex min-h-[44px] items-center gap-3 rounded-xl border border-gray-200 px-4 py-2 text-sm text-codialis-navy"
                >
                  <input
                    type="radio"
                    name="situation-compte-rendu"
                    checked={saisie.situation === situation}
                    onChange={() => setSaisie((etat) => ({ ...etat, situation }))}
                  />
                  {LIBELLES_SITUATION[situation]}
                </label>
              ))}
            </div>
            {/* Date et motif n'apparaissent qu'en cas de rupture — envisagée ou
                actée : les afficher toujours surchargerait le formulaire. */}
            {rupture && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Champ libelle="Date de la rupture">
                  <input
                    type="date"
                    value={saisie.dateRupture}
                    onChange={(evenement) =>
                      setSaisie((etat) => ({ ...etat, dateRupture: evenement.target.value }))
                    }
                    className={CLASSE_CHAMP}
                  />
                </Champ>
                <Champ libelle="Motif">
                  <input
                    type="text"
                    value={saisie.motifRupture}
                    onChange={(evenement) =>
                      setSaisie((etat) => ({ ...etat, motifRupture: evenement.target.value }))
                    }
                    className={CLASSE_CHAMP}
                  />
                </Champ>
              </div>
            )}
          </section>

          <section>
            <Champ libelle="Observations complémentaires">
              <textarea
                rows={4}
                value={saisie.observations}
                onChange={(evenement) =>
                  setSaisie((etat) => ({ ...etat, observations: evenement.target.value }))
                }
                className={CLASSE_CHAMP}
              />
            </Champ>
          </section>

          <section className="space-y-4">
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
              Signatures
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Champ libelle="Fait à">
                <input
                  type="text"
                  value={saisie.faitA}
                  onChange={(evenement) =>
                    setSaisie((etat) => ({ ...etat, faitA: evenement.target.value }))
                  }
                  className={CLASSE_CHAMP}
                />
              </Champ>
              <Champ libelle="Le">
                <input
                  type="date"
                  value={saisie.faitLe}
                  onChange={(evenement) =>
                    setSaisie((etat) => ({ ...etat, faitLe: evenement.target.value }))
                  }
                  className={CLASSE_CHAMP}
                />
              </Champ>
            </div>

            {/* Trois blocs, jamais un mot « Signature » posé là : le pavé se
                signe au doigt ou au stylet, comme l'émargement. */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <BlocSignature
                titre="Le représentant de l’entreprise"
                nom={saisie.nomEntreprise}
                onNom={(nom) => setSaisie((etat) => ({ ...etat, nomEntreprise: nom }))}
                fonction={saisie.fonctionEntreprise}
                onFonction={(fonction) =>
                  setSaisie((etat) => ({ ...etat, fonctionEntreprise: fonction }))
                }
                traceInitiale={data?.signatures?.entreprise ?? null}
                onTrace={(trace) => setTraces((etat) => ({ ...etat, entreprise: trace }))}
              />
              <BlocSignature
                titre="L’apprenant"
                nom={saisie.nomApprenant}
                onNom={(nom) => setSaisie((etat) => ({ ...etat, nomApprenant: nom }))}
                traceInitiale={data?.signatures?.apprenant ?? null}
                onTrace={(trace) => setTraces((etat) => ({ ...etat, apprenant: trace }))}
              />
              <BlocSignature
                titre="TOP FORMATION"
                nom={saisie.nomOrganisme}
                onNom={(nom) => setSaisie((etat) => ({ ...etat, nomOrganisme: nom }))}
                traceInitiale={data?.signatures?.organisme ?? null}
                onTrace={(trace) => setTraces((etat) => ({ ...etat, organisme: trace }))}
              />
            </div>
          </section>

          {messageErreur && <MessageErreur message={messageErreur} />}

          <div className="flex flex-wrap justify-end gap-3 border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={onFermer}
              disabled={enregistrer.isPending}
              className="min-h-[44px] rounded-xl px-4 py-2.5 text-sm font-semibold text-codialis-navy hover:bg-gray-50 disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={valider}
              disabled={enregistrer.isPending || saisie.compteRendu.trim().length < 3}
              className="min-h-[44px] rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:cursor-not-allowed disabled:opacity-60"
            >
              {enregistrer.isPending
                ? 'Génération…'
                : existant
                  ? 'Mettre à jour le compte rendu'
                  : 'Générer le compte rendu'}
            </button>
          </div>
        </div>
      )}
    </Modale>
  );
}

function Donnee({ libelle, valeur }: { libelle: string; valeur?: string | null }): JSX.Element {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-codialis-gray">{libelle}</dt>
      <dd className="font-semibold text-codialis-navy">
        {valeur && valeur.trim() !== '' ? valeur : 'Non renseigné'}
      </dd>
    </div>
  );
}

function Champ({
  libelle,
  aide,
  children,
}: {
  libelle: string;
  aide?: string;
  children: JSX.Element;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-semibold text-codialis-navy">{libelle}</span>
      {aide && <span className="mb-1 block text-xs text-codialis-gray">{aide}</span>}
      {children}
    </label>
  );
}

function BlocSignature({
  titre,
  nom,
  onNom,
  fonction,
  onFonction,
  traceInitiale,
  onTrace,
}: {
  titre: string;
  nom: string;
  onNom: (nom: string) => void;
  /** Seul le représentant de l'entreprise signe à un titre qui doit être dit. */
  fonction?: string;
  onFonction?: (fonction: string) => void;
  traceInitiale: string | null;
  onTrace: (trace: string | null) => void;
}): JSX.Element {
  return (
    <div className="space-y-2 rounded-2xl border border-gray-200 p-4">
      <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
        {titre}
      </div>
      <input
        type="text"
        value={nom}
        onChange={(evenement) => onNom(evenement.target.value)}
        placeholder="Nom et prénom"
        aria-label={`Nom et prénom · ${titre}`}
        className={CLASSE_CHAMP}
      />
      {onFonction && (
        <input
          type="text"
          value={fonction ?? ''}
          onChange={(evenement) => onFonction(evenement.target.value)}
          placeholder="Fonction"
          aria-label={`Fonction · ${titre}`}
          className={CLASSE_CHAMP}
        />
      )}
      <PaveSignature onChanger={onTrace} traceInitiale={traceInitiale} />
    </div>
  );
}
